import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClearAllIcon from '@mui/icons-material/ClearAll';

// MAV_SEVERITY (0..7): EMERGENCY, ALERT, CRITICAL, ERROR, WARNING, NOTICE, INFO, DEBUG
const SEVERITY_META = [
    { label: 'EMER', color: '#ff1744', bg: 'rgba(255, 23, 68, 0.18)' },
    { label: 'ALRT', color: '#ff5252', bg: 'rgba(255, 82, 82, 0.16)' },
    { label: 'CRIT', color: '#ff5722', bg: 'rgba(255, 87, 34, 0.16)' },
    { label: 'ERR',  color: '#ff7043', bg: 'rgba(255, 112, 67, 0.14)' },
    { label: 'WARN', color: '#ffb74d', bg: 'rgba(255, 183, 77, 0.14)' },
    { label: 'NOTE', color: '#81d4fa', bg: 'rgba(129, 212, 250, 0.10)' },
    { label: 'INFO', color: '#b0bec5', bg: 'rgba(176, 190, 197, 0.08)' },
    { label: 'DBG',  color: '#90a4ae', bg: 'rgba(144, 164, 174, 0.06)' },
];

const MAX_MESSAGES = 200;
const fmtTime = (ts) => {
    const d = new Date(ts);
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

export default function GcsMessages({ selectedMavId }) {
    const [messages, setMessages] = useState([]);
    const [expanded, setExpanded] = useState(false);
    const idRef = useRef(0);
    const listRef = useRef(null);

    useEffect(() => {
        const handler = (event) => {
            const d = event.detail || {};
            if (d.srcSystem !== selectedMavId) return;
            const text = (d.text || '').trim();
            if (!text) return;
            const sev = typeof d.severity === 'number' ? d.severity : 6;
            const item = { id: ++idRef.current, text, severity: sev, ts: Date.now(), srcSystem: d.srcSystem };
            setMessages((prev) => {
                const next = [item, ...prev];
                return next.length > MAX_MESSAGES ? next.slice(0, MAX_MESSAGES) : next;
            });
        };
        window.addEventListener('STATUSTEXT', handler);
        return () => window.removeEventListener('STATUSTEXT', handler);
    }, [selectedMavId]);

    useEffect(() => {
        if (expanded && listRef.current) {
            listRef.current.scrollTop = 0;
        }
    }, [messages, expanded]);

    const latest = messages[0];
    const latestMeta = latest ? (SEVERITY_META[latest.severity] || SEVERITY_META[6]) : null;

    return (
        <Box
            sx={{
                width: 360,
                maxWidth: '40vw',
                bgcolor: 'rgba(0, 0, 0, 0.72)',
                border: '1px solid rgba(255, 255, 255, 0.18)',
                borderRadius: 1,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.5)',
                backdropFilter: 'blur(2px)',
                color: '#fff',
                pointerEvents: 'auto',
                overflow: 'hidden',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    cursor: 'pointer',
                    bgcolor: latestMeta ? latestMeta.bg : 'transparent',
                    transition: 'background-color 200ms',
                }}
                onClick={() => setExpanded((v) => !v)}
            >
                <Typography
                    sx={{
                        fontSize: 9,
                        fontWeight: 700,
                        letterSpacing: 0.5,
                        color: latestMeta ? latestMeta.color : 'rgba(255,255,255,0.6)',
                        minWidth: 36,
                    }}
                >
                    {latestMeta ? latestMeta.label : 'MSG'}
                </Typography>
                <Typography
                    sx={{
                        flex: 1,
                        fontSize: 11,
                        color: '#fff',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {latest ? latest.text : 'No messages'}
                </Typography>
                <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', mx: 0.5 }}>
                    {messages.length || ''}
                </Typography>
                {expanded && messages.length > 0 && (
                    <Tooltip title="Clear" arrow>
                        <IconButton
                            size="small"
                            sx={{ color: 'rgba(255,255,255,0.7)', p: 0.25 }}
                            onClick={(e) => { e.stopPropagation(); setMessages([]); }}
                        >
                            <ClearAllIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                )}
                <IconButton size="small" sx={{ color: 'rgba(255,255,255,0.7)', p: 0.25 }}>
                    {expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                </IconButton>
            </Box>
            {expanded && (
                <Box
                    ref={listRef}
                    sx={{
                        maxHeight: 220,
                        overflowY: 'auto',
                        borderTop: '1px solid rgba(255, 255, 255, 0.12)',
                    }}
                >
                    {messages.length === 0 ? (
                        <Typography sx={{ p: 1, fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>
                            No messages
                        </Typography>
                    ) : (
                        messages.map((m) => {
                            const meta = SEVERITY_META[m.severity] || SEVERITY_META[6];
                            return (
                                <Box
                                    key={m.id}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'baseline',
                                        gap: 0.75,
                                        px: 1,
                                        py: 0.375,
                                        fontSize: 11,
                                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                                        bgcolor: meta.bg,
                                    }}
                                >
                                    <Typography component="span" sx={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', fontVariantNumeric: 'tabular-nums', minWidth: 56 }}>
                                        {fmtTime(m.ts)}
                                    </Typography>
                                    <Typography component="span" sx={{ fontSize: 9, fontWeight: 700, color: meta.color, minWidth: 36 }}>
                                        {meta.label}
                                    </Typography>
                                    <Typography component="span" sx={{ fontSize: 11, color: '#fff', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {m.text}
                                    </Typography>
                                </Box>
                            );
                        })
                    )}
                </Box>
            )}
        </Box>
    );
}
